import { MarkEmailRead, MarkEmailUnread } from "@mui/icons-material";
import { IconButton, Tooltip } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { Box } from "@mui/material";
import { AxiosResponse } from "axios";
import { format, parse } from "date-fns";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_RowSelectionState,
} from "material-react-table";
import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import i18n from "src/shared/services/languages/i18n";
import getTableLang from "src/shared/services/languages/MaterialReactTable/getTableLang";
import useChangeEmailsReadFlag from "../../data-access/useMarkEmailsRead";
import { TMail, TMailBoxEmails } from "../../types";
import { useTranslation } from "react-i18next";
import useCustomMaterialTable from "src/shared/hooks/useCustomMaterialTable";

type TMailBoxResultsProps = {
  results: AxiosResponse<TMailBoxEmails> | undefined;
  loading: boolean;
  setPaginationModel: React.Dispatch<React.SetStateAction<MRT_PaginationState>>;
  pagination: MRT_PaginationState;
  setRowSelectionModel: React.Dispatch<
    React.SetStateAction<MRT_RowSelectionState>
  >;
  rowSelection: MRT_RowSelectionState;
};
function MailBoxResults(props: TMailBoxResultsProps) {
  const { t } = useTranslation();
  const { mutate: changeEmailsReadFlag } = useChangeEmailsReadFlag();
  const {
    results,
    loading,
    setPaginationModel,
    pagination,
    setRowSelectionModel,
    rowSelection,
  } = props;
  const navigate = useNavigate();
  const columns = useMemo<MRT_ColumnDef<TMail>[]>(
    () => [
      {
        accessorFn: (row) =>
          `${format(
            parse(row?.datesent, "dd/MM/yyyy HH:mm:ss", new Date()),
            "dd/MM/yyyy",
          )} ${format(
            parse(row?.datesent, "dd/MM/yyyy HH:mm:ss", new Date()),
            "HH:mm",
          )}`,
        header: t("Ημερομηνία Αποστολής"),
      },
      {
        accessorKey: "emailType",
        header: t("Κατηγορία Μηνύματος"),
      },
      {
        accessorKey: "senderEmail",
        header: t("Ηλεκτρονική Διεύθυνση"),
      },
      {
        accessorKey: "isInValid",
        header: t("Εγκυρότητα Ηλεκτρονικής Διεύθυνσης"),
        size: 100,
        Cell: ({ cell }) => {
          const val = cell.getValue();
          const isEmailInvalid = val === 1 || val === true;
          return (
            <Box display="flex" justifyContent="center" alignItems="center">
              {!isEmailInvalid ? (
                <CheckCircleIcon color="success" />
              ) : (
                <CancelIcon color="error" />
              )}
            </Box>
          );
        },
      },
      {
        accessorKey: "subject",
        header: t("Θέμα"),
        Cell: ({ row }) => (
          <Link
            onClick={() =>
              changeEmailsReadFlag({
                emails: [
                  {
                    id: row.original.id,
                    emailType: row.original.messageTypeId,
                  },
                ],
                readFlag: false,
                showNotification: false,
              })
            }
            to={`/email/mailbox/${row.original.messageTypeId}/${row.original.id}`}
          >
            {row.original.subject}
          </Link>
        ),
      },
    ],
    [i18n.language, changeEmailsReadFlag, results?.data?.data],
  );
  const table = useCustomMaterialTable({
    columns,
    data: results?.data?.data ?? [],
    rowCount: results?.data?.recordsTotal,
    enableFilters: false,
    enableTopToolbar: true,
    renderToolbarInternalActions: () => (
      <>
        <Tooltip title={t("Επισήμανση ως Αναγνωσμένο")}>
          <IconButton
            onClick={() => {
              if (Object.keys(rowSelection).length === 0) {
                return;
              }
              setRowSelectionModel({});
              changeEmailsReadFlag({
                emails: Object.keys(rowSelection).map((key) => {
                  return { ...JSON.parse(key), readFlag: true };
                }),
                readFlag: true,
                showNotification: true,
              });
            }}
          >
            <MarkEmailRead />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("Επισήμανση ως Μη Αναγνωσμένο")}>
          <IconButton
            onClick={() => {
              if (Object.keys(rowSelection).length === 0) {
                return;
              }
              setRowSelectionModel({});
              changeEmailsReadFlag({
                emails: Object.keys(rowSelection).map((key) => {
                  return { ...JSON.parse(key), readFlag: true };
                }),
                readFlag: false,
                showNotification: true,
              });
            }}
          >
            <MarkEmailUnread />
          </IconButton>
        </Tooltip>
      </>
    ),
    enableColumnActions: false,
    enableStickyHeader: true,
    enableRowActions: true,
    renderRowActions: ({ row }) => (
      <>
        {row.original.readFlag ? (
          <Tooltip title={t("Επισήμανση ως Μη Αναγνωσμένο")}>
            <IconButton
              sx={{ zIndex: "60" }}
              disabled={!row.original.readFlag}
              onClick={(e) => {
                e.stopPropagation();
                changeEmailsReadFlag({
                  emails: [
                    {
                      id: row.original.id,
                      emailType: row.original.messageTypeId,
                    },
                  ],
                  readFlag: false,
                  showNotification: true,
                });
              }}
            >
              <MarkEmailRead />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={t("Επισήμανση ως Αναγνωσμένο")}>
            <IconButton
              sx={{ zIndex: "60" }}
              onClick={(e) => {
                e.stopPropagation();
                changeEmailsReadFlag({
                  emails: [
                    {
                      id: row.original.id,
                      emailType: row.original.messageTypeId,
                    },
                  ],
                  readFlag: true,
                  showNotification: true,
                });
              }}
            >
              <MarkEmailUnread />
            </IconButton>
          </Tooltip>
        )}
      </>
    ),
    enableSorting: false,
    localization: getTableLang(),
    enableRowSelection: true,
    getRowId: (row) => {
      return JSON.stringify({ id: row.id, emailType: row.messageTypeId });
    },
    onRowSelectionChange: setRowSelectionModel,
    initialState: {
      columnPinning: {
        left: ["mrt-row-select"],
      },
    },
    state: {
      isLoading: loading,
      showProgressBars: loading,
      pagination,
      rowSelection,
    },

    muiTableBodyRowProps: ({ row }) => ({
      sx: {
        backgroundColor: row.original.readFlag ? "#f2f6fc" : "white",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "inherit",
        },
        zIndex: "9",

        "& > td": { fontWeight: row.original.readFlag ? "normal" : "bold" },
      },
      onClick: () => {
        changeEmailsReadFlag({
          emails: [
            {
              id: row.original.id,
              emailType: row.original.messageTypeId,
            },
          ],
          readFlag: true,
          showNotification: false,
        });
        navigate(
          `/email/mailbox/${row.original.messageTypeId}/${row.original.id}`,
        );
      },
    }),
    manualPagination: true,
    onPaginationChange: setPaginationModel,
  });
  return (
    <>
      <MaterialReactTable table={table} />
    </>
  );
}

export default MailBoxResults;
